<!--
  How an item is split between the people ticked on it: evenly, by shares (2 : 1), by percent
  (33.33% / 66.67%), by fixed amounts, or typed as text. Everything is written as the same
  "Uneven split" text the engine already reads.
-->
<script lang="ts">
  import * as H from "../settings/hints.ts";
  import { untrack } from "svelte";
  import { evenPercents, readSplitUi, writeSplitUi, type Person, type SplitUi } from "@split/core";
  import Swatch from "./Swatch.svelte";
  import { formatMicro, readMoney } from "../lib/format.ts";

  let {
    value = $bindable(""),
    ticked,
    everyone,
    shares,
    errors = [],
  }: {
    value: string;
    ticked: Person[];
    everyone: Person[];
    shares: Record<string, number> | undefined;
    errors?: string[];
  } = $props();

  type Mode = SplitUi["mode"] | "text";
  const start = untrack(() => readSplitUi(value, ticked, everyone));
  let mode = $state<Mode>(untrack(() => (value.trim() && start.mode === "even" ? "text" : start.mode)));
  let shareN = $state<Record<string, number>>(start.shares);
  let pct = $state<Record<string, string>>(Object.fromEntries(Object.entries(start.percents).map(([k, v]) => [k, String(v)])));
  let amt = $state<Record<string, string>>(Object.fromEntries(Object.entries(start.amounts).map(([k, v]) => [k, (v / 100).toFixed(2)])));

  function write() {
    if (mode === "text") return;
    const ui: SplitUi = {
      mode,
      shares: shareN,
      percents: Object.fromEntries(ticked.map((p) => [p.id, Number(pct[p.id] || 0)])),
      amounts: Object.fromEntries(
        ticked.filter((p) => (amt[p.id] ?? "").trim() !== "").map((p) => [p.id, readMoney(amt[p.id]!) ?? 0]),
      ),
    };
    value = writeSplitUi(ui, ticked);
  }

  function setMode(m: Mode) {
    mode = m;
    if (m === "shares") for (const p of ticked) shareN[p.id] ??= 1;
    if (m === "percent" && ticked.some((p) => !pct[p.id])) {
      pct = Object.fromEntries(Object.entries(evenPercents(ticked)).map(([k, v]) => [k, String(v)]));
    }
    write();
  }

  // Re-write when people are ticked or unticked, so the text always names exactly them.
  $effect(() => {
    void ticked.map((p) => p.id).join();
    untrack(write);
  });

  const pctTotal = $derived(ticked.reduce((s, p) => s + Number(pct[p.id] || 0), 0));
  const shareTotal = $derived(ticked.reduce((s, p) => s + (shareN[p.id] ?? 1), 0));

  function bump(id: string, d: number) {
    shareN[id] = Math.max(0, (shareN[id] ?? 1) + d);
    write();
  }

  function thirds() {
    const [a, b] = ticked;
    if (!a || !b) return;
    pct = { [a.id]: "33.33", [b.id]: "66.67" };
    write();
  }
</script>

<div class="split">
  <div class="modes" role="group" aria-label="How to split">
    {#each [["even", "Evenly"], ["shares", "Shares"], ["percent", "Percent"], ["amounts", "Amounts"], ["text", "Type it"]] as [m, label]}
      <button type="button" aria-pressed={mode === m} onclick={() => setMode(m as Mode)} disabled={ticked.length < 2 && m !== "even" && m !== "text"}>{label}</button>
    {/each}
  </div>

  {#if ticked.length === 0}
    <p class="hint">Tick who it's for first.</p>
  {:else if mode === "even"}
    <p class="hint">{ticked.length === 1 ? "All of it goes to one person." : `Split equally between ${ticked.length} people.`}</p>
  {:else if mode === "text"}
    <input class="input" bind:value placeholder="Emily 2, Chloe 1   or   Emily $10" aria-label="Uneven split" />
    {#if H.SPLIT_WEIGHTS_HINT.trim()}<p class="hint">{H.SPLIT_WEIGHTS_HINT}</p>{/if}
  {:else}
    <ul>
      {#each ticked as p (p.id)}
        <li>
          <Swatch color={p.color} status="paid" size={16} />
          <span class="who">{p.name}</span>
          {#if mode === "shares"}
            <span class="stepper">
              <button type="button" aria-label="Less for {p.name}" onclick={() => bump(p.id, -1)}>−</button>
              <span class="num n" aria-label="{p.name}'s shares">{shareN[p.id] ?? 1}</span>
              <button type="button" aria-label="More for {p.name}" onclick={() => bump(p.id, 1)}>+</button>
            </span>
            <span class="muted frac">{shareN[p.id] ?? 1}/{shareTotal}</span>
          {:else if mode === "percent"}
            <span class="inp"><input class="input money" inputmode="decimal" bind:value={pct[p.id]} oninput={write} aria-label="{p.name}'s percent" /><span>%</span></span>
          {:else}
            <span class="inp"><span>$</span><input class="input money" inputmode="decimal" bind:value={amt[p.id]} oninput={write} placeholder="rest" aria-label="{p.name}'s amount" /></span>
          {/if}
          <span class="num amt">{shares?.[p.id] !== undefined ? formatMicro(shares[p.id]!) : ""}</span>
        </li>
      {/each}
    </ul>
    {#if mode === "percent"}
      <p class="hint" class:owes={Math.abs(pctTotal - 100) > 0.01}>
        {Math.abs(pctTotal - 100) <= 0.01 ? "Adds up to 100%." : `Adds up to ${Math.round(pctTotal * 100) / 100}%; it's scaled so the parts still cover the whole item.`}
        <button type="button" class="link" onclick={() => setMode("percent")}>Even</button>
        {#if ticked.length === 2}<button type="button" class="link" onclick={thirds}>⅓ / ⅔</button>{/if}
      </p>
    {:else if mode === "amounts"}
      {#if H.SPLIT_BLANK_HINT.trim()}<p class="hint">{H.SPLIT_BLANK_HINT}</p>{/if}
    {/if}
  {/if}
  {#each errors as e}<p class="hint owes">{e}</p>{/each}
</div>

<style>
  .split {
    display: grid;
    gap: 8px;
  }

  .modes {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    padding: 3px;
    border-radius: 10px;
    background: var(--wash);
    justify-self: start;
  }

  .modes button {
    padding: 5px 10px;
    border: 0;
    border-radius: 8px;
    background: none;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 600;
    cursor: pointer;
  }

  .modes button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.12);
  }

  .modes button:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 6px;
  }

  li {
    display: grid;
    grid-template-columns: auto 1fr auto auto auto;
    align-items: center;
    gap: 10px;
  }

  .stepper {
    display: inline-flex;
    align-items: center;
    border: 1px solid var(--rule);
    border-radius: 8px;
    overflow: hidden;
  }

  .stepper button {
    width: 30px;
    height: 30px;
    border: 0;
    background: var(--surface);
    color: var(--ink);
    font-size: 1rem;
    cursor: pointer;
  }

  .n {
    min-width: 28px;
    text-align: center;
    font-weight: 650;
  }

  .frac {
    font-size: var(--t-xs);
    min-width: 34px;
  }

  .inp {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .inp .input {
    width: 90px;
  }

  .amt {
    min-width: 64px;
    text-align: right;
    font-weight: 600;
  }

  .hint {
    margin: 0;
    font-size: var(--t-xs);
    color: var(--muted);
  }

  .hint.owes {
    color: var(--owes, #c2410c);
  }

  .link {
    margin-left: 6px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--focus, #3b7ddd);
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
</style>
